import React from 'react';
import { Badge } from '@roger/ui-components';
import { AlertTriangle } from 'lucide-react';
import {
  describeDatesOutsideEpisodeWindow,
  type TaskEpisodeWindowDates,
} from '../../utils/episodeWindow';

export type OutsideEpisodeBadgeProps = TaskEpisodeWindowDates & {
  className?: string;
};

/**
 * Flags a task whose date no longer falls inside its episode. Renders nothing
 * when the task sits inside the window, so call sites can drop it in beside the
 * task name without a conditional of their own.
 *
 * Scheduling pins a task inside its episode, but an episode-date edit can move
 * the window off tasks already on the schedule — the impact dialog warns about
 * exactly those records, and proceeding is allowed. Nothing recovers them
 * automatically, so the stranded rows say so on sight instead of looking normal.
 */
export const OutsideEpisodeBadge: React.FC<OutsideEpisodeBadgeProps> = ({
  className,
  ...dates
}) => {
  const message = describeDatesOutsideEpisodeWindow(dates);
  if (!message) return null;

  return (
    <Badge
      // No warning variant exists in the design system; these are the amber
      // warning classes already used for badges elsewhere (e.g. NOAManager).
      className={`shrink-0 gap-1 border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] text-amber-700${
        className ? ` ${className}` : ''
      }`}
      title={message}
      data-testid='outside-episode-badge'
    >
      <AlertTriangle className='h-3 w-3' aria-hidden />
      Outside Episode
    </Badge>
  );
};

export default OutsideEpisodeBadge;
